/**
 * Centralized Environment Configuration
 * 
 * This file consolidates all environment variables and provides type-safe access.
 * Feature flags automatically switch between dev/prod based on VITE_APP_ENV.
 */

import { APP_VERSION } from './appVersion';
import { resolveB2cFrontendUrl } from '../shared/seo/siteOrigin';

// ============================================================================
// ENVIRONMENT DETECTION
// ============================================================================

export const APP_ENV = import.meta.env.VITE_APP_ENV || 'development';
export const IS_DEV = APP_ENV === 'development';
export const IS_PROD = APP_ENV === 'production';

// ============================================================================
// SUPABASE CONFIGURATION
// ============================================================================

export const supabaseConfig = {
  url: import.meta.env.VITE_SUPABASE_URL || '',
  anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
};

// ============================================================================
// FIREBASE CONFIGURATION
// ============================================================================

export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
  useEmulator: import.meta.env.VITE_FIREBASE_EMULATOR === 'true',
};

// ============================================================================
// PAYMENT GATEWAY CONFIGURATION
// ============================================================================

// Razorpay Configuration
export const razorpayConfig = {
  keyId: import.meta.env.VITE_RAZORPAY_KEY_ID || '',
  keySecret: import.meta.env.VITE_RAZORPAY_KEY_SECRET || '',
  // Auto-detect environment: 'test' in dev, 'live' in prod
  environment: import.meta.env.VITE_RAZORPAY_ENV || (IS_DEV ? 'test' : 'production'),
  isTestMode: function() {
    return this.environment === 'test' || this.keyId.startsWith('rzp_test_');
  },
};

// Cashfree Configuration
import { getCashfreeSdkMode } from '../components/CreditSystem/creditCheckoutUtils';

export const cashfreeConfig = {
  environment: getCashfreeSdkMode(),
  isTestMode: function() {
    return this.environment === 'sandbox';
  },
};

// ============================================================================
// SECURITY & CAPTCHA
// ============================================================================

// Cloudflare Turnstile Configuration
export const turnstileConfig = {
  siteKey: import.meta.env.VITE_TURNSTILE_SITE_KEY || '0x4AAAAAABuVdQp0BmcoVmW0',
  // Cloudflare test keys always work: '1x00000000000000000000AA' (always passes)
  isTestMode: function() {
    return this.siteKey === '1x00000000000000000000AA' || 
           this.siteKey === '2x00000000000000000000AB' || 
           this.siteKey === '3x00000000000000000000FF';
  },
};

// ============================================================================
// FEATURE FLAGS & DEVELOPMENT SETTINGS
// ============================================================================

export const featureFlags = {
  // Credit System Bypass (DEVELOPMENT ONLY)
  bypassCreditSystem: import.meta.env.VITE_BYPASS_CREDIT_SYSTEM === 'true',
  
  // Debug & Logging
  debugMode: import.meta.env.VITE_DEBUG_MODE === 'true',
  showConsoleLogs: import.meta.env.VITE_SHOW_CONSOLE_LOGS === 'true',
  showFirebaseDiagnostics: import.meta.env.VITE_SHOW_FIREBASE_DIAGNOSTICS === 'true',
  
  // Performance Monitoring
  enablePerformanceMonitoring: import.meta.env.VITE_ENABLE_PERFORMANCE_MONITORING === 'true',
  
  // Email Verification (for testing)
  skipEmailVerification: import.meta.env.VITE_SKIP_EMAIL_VERIFICATION === 'true',
  
  // Mock Payment (for testing)
  mockPayments: import.meta.env.VITE_MOCK_PAYMENTS === 'true',
  
  // Phone Auth Test Mode (DEVELOPMENT ONLY)
  // Controls visibility of Test Mode toggle in phone signup/signin
  // Set to 'true' in dev, 'false' in production to hide test mode option
  enablePhoneTestMode: import.meta.env.VITE_PHONE_AUTH_TEST_MODE === 'true',

  // Referral hub (/referrals) — Netlify sets VITE_ENABLE_REFERRALS=true in production.
  enableReferrals: import.meta.env.VITE_ENABLE_REFERRALS === 'true',
};

// ============================================================================
// APPLICATION METADATA
// ============================================================================

export const appConfig = {
  name: import.meta.env.VITE_APP_NAME || 'GoGrabJob',
  version: APP_VERSION,
  frontendUrl: resolveB2cFrontendUrl(),
};

// ============================================================================
// API KEYS (Third-party services)
// ============================================================================

export const apiKeys = {
  groq: import.meta.env.VITE_GROQ_API_KEY || '',
};

// ============================================================================
// VALIDATION & DEBUGGING
// ============================================================================

/**
 * Validate required environment variables
 * Call this at app startup to catch configuration issues early
 */
export function validateEnvironment(): { valid: boolean; missing: string[] } {
  const required = [
    'VITE_SUPABASE_URL',
    'VITE_SUPABASE_ANON_KEY',
    'VITE_FIREBASE_API_KEY',
    'VITE_FIREBASE_PROJECT_ID',
  ];

  const missing = required.filter(key => !import.meta.env[key]);

  if (missing.length > 0) {
    console.error('❌ Missing required environment variables:', missing);
    return { valid: false, missing };
  }

  return { valid: true, missing: [] };
}

/**
 * Log current environment configuration (sanitized for security)
 * Useful for debugging deployment issues
 */
export function logEnvironmentInfo(): void {
  console.log('🌍 Environment Configuration:');
  console.log('  Environment:', APP_ENV);
  console.log('  App Version:', appConfig.version);
  console.log('  Frontend URL:', appConfig.frontendUrl);
  console.log('  Supabase URL:', supabaseConfig.url);
  console.log('  Firebase Project:', firebaseConfig.projectId);
  console.log('  Razorpay Mode:', razorpayConfig.environment);
  console.log('  Cashfree Mode:', cashfreeConfig.environment);
  console.log('  Turnstile Test Mode:', turnstileConfig.isTestMode());
  console.log('Feature Flags:');
  console.log('  Credit Bypass:', featureFlags.bypassCreditSystem);
  console.log('  Debug Mode:', featureFlags.debugMode);
  console.log('  Mock Payments:', featureFlags.mockPayments);
  console.log('  Skip Email Verification:', featureFlags.skipEmailVerification);
  console.log('  Phone Test Mode:', featureFlags.enablePhoneTestMode);
}

// Export everything as default for convenience
export default {
  env: APP_ENV,
  isDev: IS_DEV,
  isProd: IS_PROD,
  supabase: supabaseConfig,
  firebase: firebaseConfig,
  razorpay: razorpayConfig,
  cashfree: cashfreeConfig,
  turnstile: turnstileConfig,
  features: featureFlags,
  app: appConfig,
  apiKeys,
  validateEnvironment,
  logEnvironmentInfo,
};

